import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Lock } from "lucide-react";
import { Card } from "@/components/Kpi";

const BADGE_CLS = {
  NEW: "bg-emerald-600 text-white",
  MARKET: "bg-caricato text-white",
  COMPETITOR: "bg-liquidato text-white",
  REGULATION: "bg-amber-500 text-white",
  "HIGH IMPACT": "bg-red-600 text-white",
};

export const CAT_LABEL = {
  credito: "Credito al consumo", automotive: "Automotive", finanziamenti_auto: "Finanziamenti auto",
  prestiti_personali: "Prestiti personali", competitor: "Competitor", normativa: "Normativa",
  macroeconomia: "Macroeconomia", dealer: "Dealer",
};

export const dataOra = (iso) => {
  if (!iso) return "N/D";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit" });
};

export const Badge = ({ b }) => (
  <span className={`text-[9px] font-bold tracking-[0.12em] px-1.5 py-0.5 rounded ${BADGE_CLS[b] || "bg-slate-200 text-slate-700"}`}>
    {b}
  </span>
);

export const Impatto = ({ v }) => {
  if (!v) return null;
  const cls = v === "Alto" ? "text-red-600 border-red-200 bg-red-50"
    : v === "Medio" ? "text-amber-700 border-amber-200 bg-amber-50"
      : "text-slate-500 border-slate-200 bg-slate-50";
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wider border rounded px-1.5 py-0.5 ${cls}`}>
      impatto {v}
    </span>
  );
};

export function NewsCard({ a, competitorNomi = {}, compact = false }) {
  return (
    <Card className="p-4 h-full flex flex-col hover:shadow-md transition-shadow duration-200"
      data-testid={`news-card-${a.slug}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[10px] uppercase tracking-[0.14em] font-bold txt-caricato">
          {CAT_LABEL[a.categoria] || a.categoria}
        </span>
        {(a.badge || []).map((b) => <Badge key={b} b={b} />)}
      </div>
      <Link to={`/news/${a.slug}`} data-testid={`news-link-${a.slug}`}
        className="mt-2 block font-semibold text-slate-900 leading-snug hover:text-sky-700
          transition-colors duration-200 text-base">
        {a.titolo}
      </Link>
      {!compact && (
        <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">
          {a.sintesi || a.sommario_fonte || "Sintesi in preparazione."}
        </p>
      )}
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2 text-[11px] text-slate-500 mt-auto">
        <span className="truncate">
          {a.editore || a.fonte_nome}{a.paywall && <Lock size={10} className="inline ml-1 -mt-0.5" />} · {dataOra(a.pubblicato_il)}
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <Impatto v={a.impatto} />
          <span className="font-mono text-slate-400" title="Rilevanza">{a.relevance}</span>
        </span>
      </div>
      {(a.competitor || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {a.competitor.map((c) => (
            <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
              {competitorNomi[c] || c}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

const IMPATTO_STILE = {
  Alto: "bg-red-600 text-white", Medio: "bg-amber-500 text-white",
  Basso: "bg-slate-200 text-slate-600",
};

/** Riga a tutta larghezza: impatto a sinistra, anteprima al centro, categorie a destra. */
/** Logo della testata: favicon del sito, con iniziali come ripiego. */
function Logo({ a }) {
  const [errore, setErrore] = useState(false);
  const nome = a.editore || a.fonte_nome || "?";
  if (a.dominio && !errore) {
    return (
      <img src={`https://www.google.com/s2/favicons?domain=${a.dominio}&sz=64`} alt={nome}
        title={nome} onError={() => setErrore(true)} loading="lazy"
        className="w-8 h-8 rounded object-contain bg-white border border-slate-200 p-0.5" />
    );
  }
  const iniziali = nome.replace(/[^A-Za-z0-9 ]/g, " ").trim().split(/\s+/)
    .slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <span title={nome} className="w-8 h-8 rounded bg-slate-200 text-slate-600 text-[11px] font-bold
      flex items-center justify-center">
      {iniziali || "N"}
    </span>
  );
}

export function NewsRiga({ a, competitorNomi = {}, compatta = false }) {
  return (
    <Link to={`/news/${a.slug}`} data-testid={`news-riga-${a.slug}`}
      className="group block border border-slate-200 rounded-lg bg-white hover:border-sky-400
        hover:shadow-sm transition-all duration-200">
      <div className="flex flex-col sm:flex-row items-stretch">
        <div className="sm:w-24 shrink-0 flex items-center justify-center gap-2 px-3 py-2 sm:py-3
          bg-slate-50 rounded-t-lg sm:rounded-l-lg sm:rounded-tr-none border-b sm:border-b-0
          sm:border-r border-slate-100">
          <Logo a={a} />
        </div>
        <div className="flex-1 min-w-0 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <h4 className="font-semibold text-slate-900 leading-snug group-hover:text-sky-700
              transition-colors duration-200 text-base">
              {a.titolo}
            </h4>
            <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded
              ${IMPATTO_STILE[a.impatto] || "bg-slate-100 text-slate-500"}`}
              data-testid={`riga-impatto-${a.slug}`}>
              impatto {a.impatto || "N/D"}
            </span>
          </div>
          {!compatta && (
            <p className="mt-1 text-sm text-slate-600 leading-relaxed line-clamp-2">
              {a.sintesi || a.sommario_fonte}
            </p>
          )}
          <div className="mt-1.5 text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
            <span>{a.editore || a.fonte_nome}</span>
            {a.paywall && <Lock size={10} />}
            <span className="text-slate-300">·</span>
            <span>{dataOra(a.pubblicato_il)}</span>
            {(a.competitor || []).map((c) => (
              <span key={c} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                {competitorNomi[c] || c}
              </span>
            ))}
          </div>
        </div>
        <div className="sm:w-56 shrink-0 px-4 py-3 sm:border-l border-slate-100 flex flex-col
          justify-center gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.14em] font-bold txt-caricato">
            {CAT_LABEL[a.categoria] || a.categoria}
          </span>
          <span className="flex flex-wrap gap-1">
            {(a.badge || []).map((b) => <Badge key={b} b={b} />)}
          </span>
        </div>
      </div>
    </Link>
  );
}

export const LinkFonte = ({ url, testId }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" data-testid={testId}
    className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700
      hover:text-sky-900 transition-colors duration-200">
    Apri articolo originale <ArrowUpRight size={14} />
  </a>
);
